// Gera exemplos de código (curl + Python) de consumo da API pública, prontos para o
// admin enviar ao cliente. Client-side: monta a partir dos dados do token (api_key,
// hmac_secret, slug da ROTA vinculada). Espelha o fluxo REAL (OAuth-like): o HMAC assina
// o /auth UMA vez → recebe um bearer_key; as requisições de dados mandam só
// `Authorization: Bearer <bearer_key>` (sem HMAC por request).
//
// O path de dados é `/external/v1/<slug-da-rota>/`, onde o slug é o do RECURSO (a rota
// cadastrada na aba "Rotas" e vinculada ao token), nunca o de uma tabela: essa rota única
// já devolve TODAS as tabelas do recurso numa chamada, paginadas por `next_cursor`.

// Fallback da base pública quando o backend não informou base_url (tokens antigos):
// deriva da env var já configurada. Remove a barra final p/ não gerar `//external/v1/`.
const BASE_FALLBACK = (process.env.REACT_APP_API_URL ?? '').replace(/\/$/, '');

// Resolve a URL base a usar no snippet: prioriza a vinda do backend (fonte única,
// config por ambiente); só cai no env do front se ela vier vazia.
function resolveBase(baseUrl?: string): string {
  return (baseUrl ?? '').replace(/\/$/, '') || BASE_FALLBACK;
}

// Placeholder usado quando a rota do token não pôde ser resolvida (recurso ainda
// carregando, removido, ou token sem rota). NUNCA inventa um slug plausível: quem copiar
// tem que enxergar que falta preencher, em vez de mandar ao cliente um 404 disfarçado.
export const SLUG_PLACEHOLDER = '<slug-da-rota>';

export interface SnippetParams {
  apiKey: string;
  hmacSecret: string;
  // Slug da ROTA (recurso) vinculada ao token — é ele que compõe o path público
  // `GET /external/v1/<slug>/`. Um token referencia exatamente uma rota (api_recurso_id),
  // e essa rota já serve todas as suas tabelas numa chamada, então não há "N rotas" a
  // listar. Vazio/ausente (rota não resolvida) → o exemplo usa SLUG_PLACEHOLDER.
  rotaSlug?: string | null;
  // URL pública base da API (vinda do backend). Se ausente, usa o env do front.
  baseUrl?: string;
}

// Resolve o slug a estampar no exemplo. Sem rota resolvida → placeholder explícito.
function resolveSlug(rotaSlug?: string | null): string {
  return (rotaSlug ?? '').trim() || SLUG_PLACEHOLDER;
}

// Aviso no topo do exemplo quando o slug não pôde ser resolvido (`comentario` = o
// marcador de comentário da linguagem). Slug real → string vazia, exemplo limpo.
function avisoSlug(slug: string, comentario: string): string {
  if (slug !== SLUG_PLACEHOLDER) return '';
  return (
    `${comentario} ATENCAO: a rota deste token nao pode ser identificada.\n` +
    `${comentario} Troque ${SLUG_PLACEHOLDER} pelo slug da rota (aba "Rotas") antes de enviar.\n\n`
  );
}

// ── cURL ─────────────────────────────────────────────────────────────────────
export function gerarSnippetCurl({ apiKey, hmacSecret, rotaSlug, baseUrl }: SnippetParams): string {
  const BASE_PUBLICA = resolveBase(baseUrl);
  const slug = resolveSlug(rotaSlug);
  const path = `/external/v1/${slug}/`;
  return `${avisoSlug(slug, '#')}# 1) Autentique: assine o /auth com HMAC (prova posse do secret) → recebe o bearer
AUTH_PATH="/external/v1/auth/"
BODY='{"api_key": "${apiKey}"}'
TS=$(date +%s)
BODY_HASH=$(printf "%s" "$BODY" | sha256sum | cut -d" " -f1)
CANONICA="POST|\${AUTH_PATH}|\${TS}|\${BODY_HASH}"
SIG=$(printf "%s" "$CANONICA" | openssl dgst -sha256 -hmac "${hmacSecret}" | cut -d" " -f2)

BEARER=$(curl -s -X POST ${BASE_PUBLICA}\${AUTH_PATH} \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Signature: \${SIG}" \\
  -H "X-Api-Timestamp: \${TS}" \\
  -d "$BODY" | python3 -c "import sys,json;print(json.load(sys.stdin)['bearer_key'])")

# 2) Consuma os dados — só o bearer, sem HMAC
curl -s ${BASE_PUBLICA}${path} \\
  -H "Authorization: Bearer \${BEARER}"`;
}

// ── Python (stdlib) ──────────────────────────────────────────────────────────
export function gerarSnippetPython({
  apiKey,
  hmacSecret,
  rotaSlug,
  baseUrl,
}: SnippetParams): string {
  const BASE_PUBLICA = resolveBase(baseUrl);
  const slug = resolveSlug(rotaSlug);
  const path = `/external/v1/${slug}/`;
  return `${avisoSlug(slug, '#')}import hashlib, hmac, json, time, urllib.request

BASE = "${BASE_PUBLICA}"
API_KEY = "${apiKey}"
HMAC_SECRET = "${hmacSecret}"

# 1) Autentique: assine o /auth com HMAC → recebe o bearer_key
auth_path = "/external/v1/auth/"
body = json.dumps({"api_key": API_KEY}).encode()
ts = str(int(time.time()))
body_hash = hashlib.sha256(body).hexdigest()
canonica = f"POST|{auth_path}|{ts}|{body_hash}"
sig = hmac.new(HMAC_SECRET.encode(), canonica.encode(), hashlib.sha256).hexdigest()

req = urllib.request.Request(
    BASE + auth_path, data=body, method="POST",
    headers={
        "Content-Type": "application/json",
        "X-Api-Signature": sig,
        "X-Api-Timestamp": ts,
    },
)
bearer = json.load(urllib.request.urlopen(req))["bearer_key"]

# 2) Consome os dados — só o bearer, sem HMAC (paginado por cursor)
path = "${path}"
req = urllib.request.Request(BASE + path, headers={"Authorization": f"Bearer {bearer}"})
resp = json.load(urllib.request.urlopen(req))
print(resp["data"])            # linhas (só as colunas liberadas + id opaco)
print(resp["next_cursor"])     # cursor da próxima página (None = acabou)`;
}
